import { MinusIcon, PlusIcon } from '@assets/icons';
import { StationeryTemplateCategoryEnum } from '@graphql/generated';
import { useTranslation } from '@shared/core';
import { Box, Flex, TextV2, useDisclosure } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import React from 'react';

type FaqItemProps = {
  question: string;
  answer: string;
};

const FaqItem: React.FC<FaqItemProps> = ({ question, answer }) => {
  const accordion = useDisclosure();
  const Icon = accordion.isOpen ? MinusIcon : PlusIcon;

  return (
    <Flex
      borderBottom="1px solid"
      borderBottomColor="mono3"
      width="100%"
      flexDirection="column"
      itemScope
      itemProp="mainEntity"
      itemType="https://schema.org/Question"
    >
      <Flex
        as="button"
        onClick={() => accordion.onToggle()}
        padding={6}
        gap={6}
        alignItems="center"
      >
        <Icon
          width={pxToRem(24)}
          height={pxToRem(24)}
        />
        <TextV2
          typographyVariant="body2"
          fontWeight={600}
          itemProp="name"
        >
          {question}
        </TextV2>
      </Flex>
      <Flex
        flexDirection="column"
        height={accordion.isOpen ? 'auto' : 0}
        overflow="hidden"
        paddingLeft={pxToRem(72)}
        paddingRight={6}
      >
        <TextV2
          paddingBottom={6}
          typographyVariant="body2"
          itemProp="acceptedAnswer"
          itemScope
          itemType="https://schema.org/Answer"
        >
          <span itemProp="text">{answer}</span>
        </TextV2>
      </Flex>
    </Flex>
  );
};

const FaqList: React.FC = ({ children }) => {
  const { tq } = useTranslation('stationery');

  return (
    <Box
      width="100%"
      maxWidth={pxToRem(1248)}
      paddingX={6}
      paddingY={10}
      marginX="auto"
      display="grid"
      gridTemplateColumns={['1fr', null, '1fr 2fr']}
      gap={9}
    >
      <Flex
        flexDirection="column"
        gap={6}
      >
        <TextV2
          textAlign={['center', null, 'left']}
          typographyVariant="hed6"
          fontWeight={600}
        >
          {tq`Frequently Asked Questions`}
        </TextV2>
        <TextV2
          textAlign={['center', null, 'left']}
          typographyVariant="body2"
          maxWidth={pxToRem(629)}
        >
          {tq`Take a look at our most commonly asked questions and answers. For additional support, chat with us.`}
        </TextV2>
      </Flex>
      <Flex
        flexDirection="column"
        borderTop="1px solid"
        borderTopColor="mono3"
        itemType="https://schema.org/FAQPage"
        itemScope
      >
        {children}
      </Flex>
    </Box>
  );
};

type FaqSectionProps = {
  category: StationeryTemplateCategoryEnum;
};

export const FaqSection: React.FC<FaqSectionProps> = ({ category }) => {
  const { tq } = useTranslation('stationery');

  if (category === StationeryTemplateCategoryEnum.saveTheDate) {
    return (
      <FaqList>
        <FaqItem
          question={tq`When should we send Save the Dates for our wedding?`}
          answer={tq`Save the Dates are an important part of wedding planning, providing your guests with early notice of your wedding date so they can plan accordingly. For weddings where most of your guests are local, sending Save the Dates 4-6 months before your wedding is typically sufficient. This timing gives guests enough notice to mark their calendars and make any necessary arrangements. If you're hosting a destination wedding, it's best to send Save the Dates at least 9 months before the big day. This extra time allows your guests to arrange travel, accommodations, and time off from work if needed. Early notifications are especially crucial for guests who will need to book flights and hotels in advance.`}
        />
        <FaqItem
          question={tq`Whose name goes first on Save the Date?`}
          answer={tq`Traditionally, the bride's name is listed first on Save the Dates and wedding invitations. This custom is rooted in traditional etiquette where the bride's family would be the primary hosts of the wedding. However, in modern times, many couples choose to list their names in the order that feels right to them. Whether you decide to list the bride's name first or the couple's names in alphabetical order, the most important aspect is that the Save the Date clearly communicates the essential information about your wedding.`}
        />
        <FaqItem
          question={tq`What information should we include on Save the Dates?`}
          answer={tq`The purpose of Save the Dates is to provide your guests with preliminary information about your wedding. The key details to include are your names, the wedding date, and the location (city or area). Since Save the Dates are less formal than the official wedding invitations, you don't need to include as much detail. It's also a good idea to mention that formal invitations will follow with more comprehensive information. Including your wedding website URL on the Save the Date can be very helpful. This allows guests to access additional information, such as travel details, accommodation options, and updates about your wedding.`}
        />
        <FaqItem
          question={tq`Is it okay to just send online Save the Dates?`}
          answer={tq`Yes, sending digital Save the Dates is a perfectly acceptable and increasingly popular option. Digital Save the Dates are cost-effective, environmentally friendly, and convenient. They can also help streamline your wedding planning process by linking directly to your wedding website, where guests can RSVP and access further details. However, keep in mind that some guests, especially those who are less tech-savvy, might need a reminder to check their email. To ensure that everyone receives your Save the Date, consider sending a follow-up message or providing an alternate way for guests to confirm receipt.`}
        />
        <FaqItem
          question={tq`Do we need to follow a specific design or style for Save the Dates?`}
          answer={tq`There is no strict rule regarding the design or style of Save the Dates. The design should reflect your personal taste and the theme of your wedding. Save the Dates can be formal, casual, whimsical, or traditional, depending on your preference. Many couples choose designs that align with their wedding theme or that give guests a sneak peek of the style and tone of the event. Whether you opt for a minimalist design, a vibrant photo, or a classic script, the goal is to convey the essential information while giving your guests a hint of what's to come.`}
        />
        <FaqItem
          question={tq`Do I put my wedding website URL on Save the Dates?`}
          answer={tq`Though not required, it's certainly helpful. Because Save the Dates should be sent out 4 to 6 months before your wedding day, and are considered less formal than printed wedding invitations, it's best you complete your wedding website by that time. This way, you can add the URL to your Save the Dates and give your guests plenty of time to absorb all your wedding details.`}
        />
        <FaqItem
          question={tq`Can we include photos on our Save the Dates?`}
          answer={tq`Including a photo on your Save the Date can add a personal touch and make the announcement more memorable. Many couples choose to feature an engagement photo or a favorite picture that represents their relationship. Photos can make your Save the Date stand out and give guests a glimpse of the couple they're celebrating. If you decide to include a photo, ensure it complements the design and doesn't overwhelm the important details. A well-chosen photo can enhance the overall aesthetic and make your Save the Date more engaging.`}
        />
        <FaqItem
          question={tq`How do I word my Save the Date?`}
          answer={tq`The wording of your Save the Date should be clear and concise, focusing on the essential information. A simple example of Save the Date wording might be, "Save the Date: [Your Names], [Wedding Date], [Location]. Formal invitation to follow." You can also add a personal touch by including a brief message or quote that reflects your personality. The key is to provide your guests with the crucial details while keeping the tone of the message in line with the style of your wedding.`}
        />
        <FaqItem
          question={tq`Do Save the Dates and invitations have to match?`}
          answer={tq`While it's not necessary for Save the Dates and invitations to match exactly, having a cohesive look can be visually appealing. Many couples choose to align the design elements of their Save the Dates with their wedding invitations to create a consistent theme. However, it's perfectly fine to have different designs as long as they complement each other. The important thing is that both pieces of correspondence clearly convey the necessary information and reflect your wedding style.`}
        />
        <FaqItem
          question={tq`What size should the Save the Date card be?`}
          answer={tq`Save the Date cards typically come in standard sizes such as 5x7 inches, which is a common and practical size for mailing. However, you have the flexibility to choose a size that suits your design and budget. Smaller sizes like 4x6 inches or larger formats like 6x8 inches are also options depending on how you want to present your Save the Date. Make sure the size you choose aligns with your overall design and is easy for your guests to handle and store.`}
        />
        <FaqItem
          question={tq`How do I collect addresses for my Save the Dates?`}
          answer={tq`Joy's built-in Contact Collector makes it easy to gather all your guests' contact information in your guest list. Simply share your magic link with guests, they fill out a quick form, and your guest list grows automagically. Every Joy Save the Date includes free recipient and return address printing that integrates seamlessly with your contact list.`}
        />
        <FaqItem
          question={tq`Are Save the Dates necessary?`}
          answer={tq`While it's ultimately your decision, we highly recommend that Save the Dates are a part of your suite of wedding announcements. This way, you'll ensure your guests have access to the who, what, where, and when of your wedding as soon as you've determined these details. Plus, by including your wedding website link on your Save the Dates, you'll enable your guests to learn more about the wedding and RSVP online.`}
        />
        <FaqItem
          question={tq`Can you do Save the Dates and invitations together?`}
          answer={tq`Yes, coordinating your Save the Dates and invitations can create a unified look for your wedding announcements. Many couples choose to use matching or complementary designs for both Save the Dates and invitations to maintain consistency. This approach helps to set the tone for the wedding and provides a cohesive visual experience for your guests. You can work with the same designer or template provider to ensure that your Save the Dates and invitations align seamlessly.`}
        />
        <FaqItem
          question={tq`Why choose Joy's Wedding Save the Dates`}
          answer={tq`Choosing the right Save the Date template is an essential step in your wedding planning process. It sets the stage for the rest of your wedding communications and provides your guests with the first glimpse of what's to come. Joy offers a wide range of Save the Date templates that cater to various styles and preferences. Whether you're envisioning a rustic countryside wedding, a glamorous city celebration, or a laid-back beach event, Joy has a template that fits your theme. Joy's Save the Date templates are designed with both style and functionality in mind. You can select from hundreds of beautifully crafted designs, each offering unique elements that can be customized to suit your wedding theme. From elegant calligraphy to playful illustrations, Joy provides options that cater to every taste. Additionally, Joy allows you to incorporate your favorite photos into the design, adding a personal touch that makes your Save the Date even more special.`}
        />
        <FaqItem
          question={tq`What does Joy cost?`}
          answer={tq`Joy wedding websites, planning tools, registry, and online RSVP are free! Paper wedding invitations come in a range of price and style options, and all include free back-of-card printing and free recipient and return address printing. On a tight budget? Mix and match Joy's digital and paper cards to save money on paper and postage. Hundreds of digital wedding invitation designs are available to send for free.`}
        />
        <FaqItem
          question={tq`Do you have a support team we can reach out to if we have questions?`}
          answer={tq`Absolutely! Our amazing support team works hard around the clock, every day of the week, including weekends, to make sure that your questions are answered. You can send us an email at support@withjoy.com.`}
        />
        <FaqItem
          question={tq`Are Save the Date magnets essential?`}
          answer={tq`Save the Date magnets are not strictly necessary, and they can be more expensive than traditional paper cards. If you're on a tighter budget, it's perfectly fine to opt for paper Save the Dates instead. Most guests won't mind whether it's a magnet or paper; the key is ensuring the Save the Date reflects your style and provides the essential wedding details.`}
        />
        <FaqItem
          question={tq`Do you send save-the-dates to everyone?`}
          answer={tq`You should only send save-the-dates to individuals you plan to invite to your wedding. Anyone who receives a save-the-date should also be sent a wedding invitation.`}
        />
      </FaqList>
    );
  }

  return null;
};
